/**
 * Knowledge Base Cache Service
 * 
 * Caches extracted PDF text in IndexedDB to avoid re-processing on every load.
 * Uses versioning to detect when PDFs change and invalidate cache.
 */

const DB_NAME = 'VesselMindCache';
const DB_VERSION = 1;
const STORE_NAME = 'knowledgeBase';
const CACHE_KEY = 'pdfKnowledgeBase';
const VERSION_KEY = 'cacheVersion';

// Increment this when PDFs are updated to force cache invalidation
const CURRENT_VERSION = '1.0.0';

/**
 * Initialize IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('❌ Failed to open IndexedDB:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
        console.log('✅ Created IndexedDB object store');
      }
    };
  });
};

/**
 * Check if cached knowledge base exists and is current version
 * @returns {Promise<boolean>}
 */
export const isCacheValid = async () => {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      // Check version
      const versionRequest = store.get(VERSION_KEY);
      
      versionRequest.onsuccess = () => {
        const cachedVersion = versionRequest.result;
        
        if (!cachedVersion || cachedVersion !== CURRENT_VERSION) {
          console.log('📦 Cache invalid or outdated');
          resolve(false);
          return;
        }
        
        // Check if knowledge base exists
        const dataRequest = store.get(CACHE_KEY);
        
        dataRequest.onsuccess = () => {
          const hasData = !!dataRequest.result;
          if (hasData) {
            console.log('✅ Valid cache found');
          } else {
            console.log('📦 No cache data found');
          }
          resolve(hasData);
        };
        
        dataRequest.onerror = () => {
          console.error('❌ Error checking cache:', dataRequest.error);
          resolve(false);
        };
      };
      
      versionRequest.onerror = () => {
        console.error('❌ Error checking version:', versionRequest.error);
        resolve(false);
      };
      
      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('❌ Cache validation error:', error);
    return false;
  }
};

/**
 * Load knowledge base from cache
 * @returns {Promise<Array|null>}
 */
export const loadFromCache = async () => {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(CACHE_KEY);
      
      request.onsuccess = () => {
        const data = request.result;
        
        if (data) {
          console.log('✅ Loaded knowledge base from cache');
          console.log(`📊 ${data.length} documents loaded instantly`);
          resolve(data);
        } else {
          console.log('📦 No cached data found');
          resolve(null);
        }
      };
      
      request.onerror = () => {
        console.error('❌ Error loading from cache:', request.error);
        resolve(null);
      };
      
      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('❌ Cache load error:', error);
    return null;
  }
};

/**
 * Save knowledge base to cache
 * @param {Array} knowledgeBase - Extracted PDF knowledge base
 * @returns {Promise<boolean>}
 */
export const saveToCache = async (knowledgeBase) => {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Save knowledge base
      const dataRequest = store.put(knowledgeBase, CACHE_KEY);
      
      dataRequest.onsuccess = () => {
        // Save version
        const versionRequest = store.put(CURRENT_VERSION, VERSION_KEY);
        
        versionRequest.onsuccess = () => {
          console.log('✅ Knowledge base cached successfully');
          console.log(`💾 ${knowledgeBase.length} documents saved to cache`);
          const totalWords = knowledgeBase.reduce((sum, doc) => sum + (doc.wordCount || 0), 0);
          console.log(`💾 ${totalWords.toLocaleString()} words cached`);
          resolve(true);
        };
        
        versionRequest.onerror = () => {
          console.error('❌ Error saving version:', versionRequest.error);
          resolve(false);
        };
      };
      
      dataRequest.onerror = () => {
        console.error('❌ Error saving to cache:', dataRequest.error);
        resolve(false);
      };
      
      transaction.onerror = () => {
        console.error('❌ Transaction error:', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('❌ Cache save error:', error);
    return false;
  }
};

/**
 * Clear all cached data (useful for forcing re-extraction)
 * @returns {Promise<boolean>}
 */
export const clearCache = async () => {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('🗑️ Cache cleared successfully');
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('❌ Error clearing cache:', request.error);
        resolve(false);
      };
      
      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('❌ Cache clear error:', error);
    return false;
  }
};

/**
 * Get cache statistics
 * @returns {Promise<object>}
 */
export const getCacheStats = async () => {
  try {
    const isValid = await isCacheValid();
    
    if (!isValid) {
      return {
        exists: false,
        version: null,
        documentCount: 0,
        totalWords: 0
      };
    }
    
    const data = await loadFromCache();
    
    if (!data) {
      return {
        exists: false,
        version: null,
        documentCount: 0,
        totalWords: 0
      };
    }
    
    return {
      exists: true,
      version: CURRENT_VERSION,
      documentCount: data.length,
      totalWords: data.reduce((sum, doc) => sum + (doc.wordCount || 0), 0),
      cachedAt: data[0]?.loadedAt || null
    };
  } catch (error) {
    console.error('❌ Error getting cache stats:', error);
    return {
      exists: false,
      version: null,
      documentCount: 0,
      totalWords: 0
    };
  }
};

export { CURRENT_VERSION };

